import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Heart, MapPin, Fuel, Gauge, Calendar, Phone, X, Search, Zap, Settings, Hash, Shield, ChevronDown, SlidersHorizontal, Star } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { FaWhatsapp } from "react-icons/fa";
import verifiedBadge from "../assets/verified.png";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 6;

const VerifiedBadge = () => (
  <div className="absolute top-2 left-2 z-10">
    <img src={verifiedBadge} alt="Verified" className="h-5 w-auto object-contain drop-shadow-sm" style={{ maxWidth: '88px' }} />
  </div>
);

const SearchableDropdown = ({ options, value, onValueChange, placeholder, searchPlaceholder }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const filtered = options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));
  const selected = options.find(o => o.value === value);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-3 py-2.5 border rounded-xl bg-white text-sm hover:border-gray-400 transition-all shadow-sm">
        <span className={selected ? 'text-gray-800 font-medium' : 'text-gray-400'}>{selected ? selected.label : placeholder}</span>
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-xl shadow-xl max-h-72 overflow-hidden">
          <div className="p-2 border-b bg-gray-50">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input type="text" placeholder={searchPlaceholder} value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-8 pr-3 py-1.5 text-xs border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white" onClick={e => e.stopPropagation()} autoFocus />
            </div>
          </div>
          <div className="overflow-y-auto max-h-52">
            <button className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 text-gray-500 border-b" onClick={() => { onValueChange(''); setOpen(false); setSearch(''); }}>{placeholder}</button>
            {filtered.map(o => (
              <button key={o.value} className={`w-full text-left px-3 py-2.5 text-sm hover:bg-blue-50 transition-colors ${value === o.value ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-800'}`} onClick={() => { onValueChange(o.value); setOpen(false); setSearch(''); }}>
                {o.logo && <img src={o.logo} alt={o.label} className="inline w-5 h-5 object-contain mr-2 rounded" />}
                {o.label}
              </button>
            ))}
            {filtered.length === 0 && <p className="text-center text-xs text-gray-400 py-4">No results found</p>}
          </div>
        </div>
      )}
    </div>
  );
};

const MobileFilterPanel = ({ open, onClose, children }) => (
  <AnimatePresence>
    {open && (
      <>
        <motion.div key="backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
        <motion.div key="sheet" initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} transition={{ type: 'spring', damping: 28, stiffness: 280 }} className="fixed bottom-0 left-0 right-0 z-50 bg-[#eef1f6] rounded-t-2xl max-h-[85vh] flex flex-col">
          <div className="flex-shrink-0 px-5 pt-3 pb-4 border-b border-gray-200">
            <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mb-3" />
            <div className="flex items-center justify-between">
              <span className="text-base font-semibold text-gray-800">Filters</span>
              <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-200 transition"><X className="h-4 w-4 text-gray-600" /></button>
            </div>
          </div>
          <div className="overflow-y-auto flex-1 px-5 pb-6 pt-2">{children}</div>
          <div className="flex-shrink-0 px-5 pt-3 pb-[calc(0.75rem+60px)] border-t border-gray-200 bg-[#eef1f6]">
            <Button onClick={onClose} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl h-11 font-semibold">Apply Filters</Button>
          </div>
        </motion.div>
      </>
    )}
  </AnimatePresence>
);

// ─── Shared wishlist cache ────────────────────────────────────────────────────
const WISHLIST_KEY = 'x7_wishlist_ids';
const readCache  = () => { try { const c = sessionStorage.getItem(WISHLIST_KEY); return c ? new Set(JSON.parse(c)) : null; } catch { return null; } };
const writeCache = (ids) => { try { sessionStorage.setItem(WISHLIST_KEY, JSON.stringify([...ids])); } catch {} };

const Browse = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [searchParams] = useSearchParams();

  // ─── Page state ───────────────────────────────────────────────────────────
  const [selectedCity, setSelectedCity] = useState('Belgaum');
  const [cities, setCities]             = useState([]);
  const [vehicles, setVehicles]         = useState([]);
  const [brands, setBrands]             = useState([]);
  const [models, setModels]             = useState([]);
  const [wishlistedIds, setWishlistedIds] = useState(new Set());
  const [vehicleSegments, setVehicleSegments] = useState([]);
  const [skip, setSkip]                 = useState(0);
  const [hasMore, setHasMore]           = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const fetchSessionRef = useRef(0);
  const sentinelRef     = useRef(null);

  // ─── Price: visual state updates instantly; committed state triggers API ──
  const MIN_PRICE = 5000;
  const MAX_PRICE = 8000000;
  const [priceRange, setPriceRange]           = useState([MIN_PRICE, MAX_PRICE]);
  const [committedPrice, setCommittedPrice]   = useState([MIN_PRICE, MAX_PRICE]);

  const [sortBy, setSortBy] = useState('recently_added');
  const [filters, setFilters] = useState({
    vehicle_type: searchParams.get('type') || '',
    vehicle_segment: '', ev_subtype: '', fuel_type: '',
    brand_id: '', model_id: '', transmission: '', color: '',
    ownership: null, verified_only: false, has_insurance: null,
    has_puc: null, is_choice_number: searchParams.get('is_choice_number') === 'true',
  });

  const searchQuery       = searchParams.get('q')       || '';
  const segmentSearch     = searchParams.get('segment') || '';
  const registrationSearch = searchParams.get('reg')   || '';

  // ─── Build query params ───────────────────────────────────────────────────
  const buildParams = useCallback((skipVal) => {
    const params = new URLSearchParams({
      city: selectedCity, vehicle_status: 'approved',
      sort_by: sortBy, limit: PAGE_SIZE, skip: skipVal,
    });
    Object.entries(filters).forEach(([key, value]) => {
      if (['verified_only', 'has_insurance', 'has_puc'].includes(key)) return;
      if (value !== '' && value !== null && value !== false) params.set(key, value);
    });
    if (searchQuery)         params.set('search', searchQuery);
    if (segmentSearch)       params.set('vehicle_segment', segmentSearch);
    if (registrationSearch)  params.set('registration_number', registrationSearch);
    if (filters.is_choice_number) params.set('is_choice_number', 'true');
    params.set('min_price', committedPrice[0].toString());
    params.set('max_price', committedPrice[1].toString());
    return params;
  }, [selectedCity, sortBy, filters, searchQuery, segmentSearch, registrationSearch, committedPrice]);

  // ─── Client-side filters (insurance/puc not sent to server) ──────────────
  const applyClientFilter = useCallback((list) => {
    let r = list;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      r = r.filter(v => [v.brand_name, v.model_name, v.variant_name, v.fuel_type, v.transmission, v.color]
        .some(s => s?.toLowerCase().includes(q)));
    }
    if (filters.verified_only)      r = r.filter(v => v.verified_by_x7autoz === true);
    if (filters.has_insurance === true)  r = r.filter(v => v.has_insurance === true);
    if (filters.has_insurance === false) r = r.filter(v => v.has_insurance === false);
    if (filters.has_puc === true)   r = r.filter(v => v.has_puc === true);
    if (filters.has_puc === false)  r = r.filter(v => v.has_puc === false);
    if (filters.is_choice_number)   r = r.filter(v => v.is_choice_number === true);
    return r;
  }, [searchQuery, filters.verified_only, filters.has_insurance, filters.has_puc, filters.is_choice_number]);

  // ─── CRITICAL FIX: Vehicles fetch starts IMMEDIATELY on mount ─────────────
  // Sidebar data (cities, brands, models) loads in parallel — never blocks vehicles.
  // Wishlist loads from cache instantly then refreshes in background.
  useEffect(() => {
    // 1. Load vehicles immediately
    const session = ++fetchSessionRef.current;
    setVehicles([]); setSkip(0); setHasMore(true); setIsInitialLoading(true);

    const fetchVehicles = async () => {
      try {
        const res = await axios.get(`${API}/vehicles?${buildParams(0)}`);
        if (fetchSessionRef.current !== session) return;
        setVehicles(applyClientFilter(res.data.vehicles));
        setHasMore(res.data.vehicles.length === PAGE_SIZE);
        setSkip(PAGE_SIZE);
      } catch {
        if (fetchSessionRef.current === session) toast.error('Failed to load vehicles');
      } finally {
        if (fetchSessionRef.current === session) setIsInitialLoading(false);
      }
    };

    fetchVehicles();

    // 2. Load sidebar data in parallel — completely independent of vehicle fetch
    axios.get(`${API}/cities`).then(r => setCities(r.data.cities)).catch(() => {});
    axios.get(`${API}/brands`).then(r => setBrands(r.data.brands)).catch(() => {});
    axios.get(`${API}/models`).then(r => setModels(r.data.models)).catch(() => {});

    // 3. Wishlist: show cache instantly, background refresh
    if (user && token) {
      const cached = readCache();
      if (cached) setWishlistedIds(cached);
      axios.get(`${API}/wishlist`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => {
          const ids = new Set(r.data.vehicles.map(v => v.id));
          setWishlistedIds(ids);
          writeCache(ids);
        })
        .catch(() => {});
    }
  }, [filters, selectedCity, committedPrice, searchQuery, segmentSearch, registrationSearch, sortBy]);

  // Keep URL params → filter state in sync (type, choice-number search from Header)
  useEffect(() => {
    const type = searchParams.get('type');
    if (type) setFilters(prev => ({ ...prev, vehicle_type: type }));

    const isChoiceNumber = searchParams.get('is_choice_number') === 'true';
    if (isChoiceNumber) setFilters(prev => ({ ...prev, is_choice_number: true }));
  }, [searchParams]);

  // Fetch models when brand changes
  useEffect(() => {
    if (!filters.brand_id) return;
    axios.get(`${API}/models?brand_id=${filters.brand_id}&limit=50`)
      .then(r => setModels(r.data.models))
      .catch(() => {});
  }, [filters.brand_id]);

  // Fetch segments when vehicle type changes
  useEffect(() => {
    if (!filters.vehicle_type) { setVehicleSegments([]); return; }
    axios.get(`${API}/vehicle-segments?vehicle_type=${filters.vehicle_type}`)
      .then(r => setVehicleSegments(r.data.segments || []))
      .catch(() => {});
  }, [filters.vehicle_type]);

  // ─── Infinite scroll ──────────────────────────────────────────────────────
  const loadMore = useCallback(async () => {
    if (isFetchingMore || !hasMore || isInitialLoading) return;
    const session = fetchSessionRef.current;
    setIsFetchingMore(true);
    try {
      const res = await axios.get(`${API}/vehicles?${buildParams(skip)}`);
      if (fetchSessionRef.current !== session) return;
      const filtered = applyClientFilter(res.data.vehicles);
      setVehicles(prev => {
        const ids = new Set(prev.map(v => v.id));
        return [...prev, ...filtered.filter(v => !ids.has(v.id))];
      });
      setHasMore(res.data.vehicles.length === PAGE_SIZE);
      setSkip(prev => prev + PAGE_SIZE);
    } catch {} finally {
      if (fetchSessionRef.current === session) setIsFetchingMore(false);
    }
  }, [isFetchingMore, hasMore, isInitialLoading, skip, buildParams, applyClientFilter]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMore(); },
      { threshold: 0.1, rootMargin: '200px' }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [loadMore]);

  // ─── Wishlist toggle ──────────────────────────────────────────────────────
  const handleAddToWishlist = async (vehicleId) => {
    if (!user) { navigate('/login'); return; }
    try {
      if (wishlistedIds.has(vehicleId)) {
        await axios.delete(`${API}/wishlist/${vehicleId}`, { headers: { Authorization: `Bearer ${token}` } });
        setWishlistedIds(prev => { const s = new Set(prev); s.delete(vehicleId); writeCache(s); return s; });
        toast.success('Removed from wishlist');
      } else {
        await axios.post(`${API}/wishlist?vehicle_id=${vehicleId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
        setWishlistedIds(prev => { const s = new Set([...prev, vehicleId]); writeCache(s); return s; });
        toast.success('Added to wishlist!');
      }
    } catch { toast.error('Failed to update wishlist'); }
  };

  const clearFilters = () => {
    setFilters({ vehicle_type: '', vehicle_segment: '', ev_subtype: '', fuel_type: '', brand_id: '', model_id: '', transmission: '', color: '', ownership: null, verified_only: false, has_insurance: null, has_puc: null, is_choice_number: false });
    setPriceRange([MIN_PRICE, MAX_PRICE]);
    setCommittedPrice([MIN_PRICE, MAX_PRICE]);
    setSortBy('recently_added');
    setVehicleSegments([]);
    navigate('/browse');
  };

  const formatPrice = (v) => {
    if (v >= 10000000) return `₹${(v/10000000).toFixed(1)} Cr`;
    if (v >= 100000)   return `₹${(v/100000).toFixed(1)} L`;
    if (v >= 1000)     return `₹${(v/1000).toFixed(0)} K`;
    return `₹${v}`;
  };
  const getFuelColor = (fuel) => {
    if (!fuel) return "text-gray-500";
    const t = fuel.toLowerCase();
    if (t.includes("petrol")||t.includes("cng")) return "text-sky-600";
    if (t.includes("diesel"))   return "text-orange-600";
    if (t.includes("electric")) return "text-green-600";
    if (t.includes("hybrid"))   return "text-blue-700";
    return "text-gray-500";
  };

  const vehicleTypes  = [{ value: 'car', label: 'Cars' }, { value: 'bike', label: 'Bikes' }, { value: 'ev', label: 'Electric' }, { value: 'commercial', label: 'Commercial' }];
  const evSubtypes    = [{ value: 'car', label: 'EV Car' }, { value: 'bike', label: 'EV Bike' }, { value: 'commercial', label: 'EV Commercial' }];
  const fuelTypes     = [{ value: 'Petrol', label: 'Petrol' }, { value: 'Diesel', label: 'Diesel' }, { value: 'CNG', label: 'CNG' }, { value: 'Hybrid', label: 'Hybrid' }];
  const transmissions = [{ value: 'Manual', label: 'Manual' }, { value: 'Automatic', label: 'Automatic' }, { value: 'AMT', label: 'AMT' }, { value: 'CVT', label: 'CVT' }];
  const colors        = [{ value: 'Yellow & Golden', label: 'Yellow & Golden' }, { value: 'White', label: 'White' }, { value: 'Beige, Bronze & Brown', label: 'Beige, Bronze & Brown' }, { value: 'Maroon & Red', label: 'Maroon & Red' }, { value: 'Silver', label: 'Silver' }, { value: 'Gray & Black', label: 'Gray & Black' }, { value: 'Blue', label: 'Blue' }, { value: 'Green & Orange', label: 'Green & Orange' }];
  const sortOptions   = [{ value: 'recently_added', label: 'Recently Added' }, { value: 'price_low_high', label: 'Price: Low to High' }, { value: 'price_high_low', label: 'Price: High to Low' }, { value: 'kms_low_high', label: 'KMs: Low to High' }, { value: 'year_new_old', label: 'Year: Newest First' }];
  const filteredBrands  = filters.vehicle_type ? brands.filter(b => b.category === filters.vehicle_type) : brands;
  const filteredModels  = filters.brand_id ? models.filter(m => m.brand_id === filters.brand_id) : models;
  const brandOptions    = filteredBrands.map(b => ({ value: b.id, label: b.name, logo: b.logo }));
  const modelOptions    = filteredModels.map(m => ({ value: m.id, label: m.name }));

  const activeFilterCount = [
    filters.vehicle_type, filters.vehicle_segment, filters.ev_subtype, filters.fuel_type,
    filters.brand_id, filters.model_id, filters.transmission, filters.color,
    filters.ownership, filters.verified_only,
    filters.has_insurance !== null, filters.has_puc !== null, filters.is_choice_number,
  ].filter(Boolean).length + (committedPrice[0] !== MIN_PRICE || committedPrice[1] !== MAX_PRICE ? 1 : 0);

  const SkeletonCard = () => (
    <Card className="rounded-xl overflow-hidden border border-gray-200 animate-pulse">
      <div className="h-52 bg-gray-200" />
      <CardContent className="p-3 space-y-3">
        <div className="h-4 bg-gray-200 rounded w-3/4" />
        <div className="h-3 bg-gray-200 rounded w-1/2" />
        <div className="grid grid-cols-3 gap-2 mt-3">{[1,2,3,4,5,6].map(i=><div key={i} className="h-8 bg-gray-200 rounded"/>)}</div>
        <div className="flex gap-2 mt-2"><div className="flex-1 h-9 bg-gray-200 rounded"/><div className="flex-1 h-9 bg-gray-200 rounded"/></div>
      </CardContent>
    </Card>
  );

  const FilterBody = ({ onClear }) => (
    <>
      <div className="flex items-center justify-between mb-5 lg:mb-6">
        <h2 className="text-lg font-semibold text-gray-800 hidden lg:block">Filters</h2>
        <Button variant="ghost" size="sm" onClick={() => { clearFilters(); onClear?.(); }} className="text-blue-600 font-medium hover:bg-transparent ml-auto">CLEAR ALL</Button>
      </div>

      <div className="mb-6">
        <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Sort Results</h3>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="bg-white border-none rounded-xl h-12 shadow-sm"><SelectValue /></SelectTrigger>
          <SelectContent>{sortOptions.map(o=><SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {/* Price slider: visual update instant, API only fires on mouse-up */}
      <div className="mb-6">
        <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Price Range</h3>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <Slider
            value={priceRange}
            onValueChange={setPriceRange}
            onValueCommit={setCommittedPrice}
            min={MIN_PRICE} max={MAX_PRICE} step={10000}
            className="mb-4"
          />
          <div className="flex justify-between gap-3 text-xs">
            <div className="flex flex-col w-full"><span className="text-gray-400 mb-1">MIN</span><span className="bg-gray-100 px-3 py-2 rounded-lg text-sm font-medium text-gray-700">{formatPrice(priceRange[0])}</span></div>
            <div className="flex flex-col w-full"><span className="text-gray-400 mb-1">MAX</span><span className="bg-gray-100 px-3 py-2 rounded-lg text-sm font-medium text-gray-700">{formatPrice(priceRange[1])}</span></div>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Verification Status</h3>
        <button onClick={() => setFilters(prev => ({ ...prev, verified_only: !prev.verified_only }))} className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${filters.verified_only ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
          <Shield className={`h-5 w-5 flex-shrink-0 ${filters.verified_only ? 'text-blue-600' : 'text-gray-400'}`} />
          <div className="text-left"><p className="text-sm font-semibold text-gray-800">Verified Vehicles Only</p><p className="text-xs text-gray-500">Show only X7 Autoz verified</p></div>
          <div className={`ml-auto w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${filters.verified_only ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>{filters.verified_only && <div className="w-2.5 h-2.5 rounded-full bg-white" />}</div>
        </button>
      </div>

      <div className="mb-6">
        <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Insurance</h3>
        <div className="flex gap-2">
          {[{ label: 'Any', value: null }, { label: 'Insured', value: true }, { label: 'No Insurance', value: false }].map(opt => (
            <button key={String(opt.value)} onClick={() => setFilters(prev => ({ ...prev, has_insurance: opt.value }))}
              className={`flex-1 py-2 rounded-xl text-xs font-medium border-2 transition-all ${filters.has_insurance === opt.value ? (opt.value === true ? 'border-blue-500 bg-blue-50 text-blue-700' : opt.value === false ? 'border-red-400 bg-red-50 text-red-700' : 'border-gray-400 bg-gray-100 text-gray-700') : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-6">
        <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">PUC Certificate</h3>
        <div className="flex gap-2">
          {[{ label: 'Any', value: null }, { label: 'Valid PUC', value: true }, { label: 'No PUC', value: false }].map(opt => (
            <button key={String(opt.value)} onClick={() => setFilters(prev => ({ ...prev, has_puc: opt.value }))}
              className={`flex-1 py-2 rounded-xl text-xs font-medium border-2 transition-all ${filters.has_puc === opt.value ? (opt.value === true ? 'border-green-500 bg-green-50 text-green-700' : opt.value === false ? 'border-red-400 bg-red-50 text-red-700' : 'border-gray-400 bg-gray-100 text-gray-700') : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-6">
        <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Registration Number</h3>
        <button onClick={() => setFilters(prev => ({ ...prev, is_choice_number: !prev.is_choice_number }))} className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${filters.is_choice_number ? 'border-orange-500 bg-orange-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
          <Star className={`h-5 w-5 flex-shrink-0 ${filters.is_choice_number ? 'text-orange-500 fill-orange-500' : 'text-gray-400'}`} />
          <div className="text-left"><p className="text-sm font-semibold text-gray-800">Choice Numbers Only</p><p className="text-xs text-gray-500">Fancy / VIP registration plates</p></div>
          <div className={`ml-auto w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${filters.is_choice_number ? 'border-orange-500 bg-orange-500' : 'border-gray-300'}`}>{filters.is_choice_number && <div className="w-2.5 h-2.5 rounded-full bg-white" />}</div>
        </button>
      </div>

      <div className="mb-6">
        <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Vehicle Type</h3>
        <div className="flex flex-wrap gap-2">{vehicleTypes.map(type=>(<button key={type.value} onClick={()=>setFilters({...filters,vehicle_type:filters.vehicle_type===type.value?"":type.value,vehicle_segment:"",ev_subtype:"",brand_id:"",model_id:"",fuel_type:""})} className={`px-4 py-2 rounded-full text-sm font-medium transition ${filters.vehicle_type===type.value?"bg-blue-600 text-white":"bg-white text-gray-700"}`}>{type.label}</button>))}</div>
      </div>

      {filters.vehicle_type==="ev"&&(<motion.div initial={{opacity:0,height:0}} animate={{opacity:1,height:"auto"}} className="mb-6"><h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">EV Type</h3><div className="space-y-2">{evSubtypes.map(s=>(<button key={s.value} onClick={()=>setFilters({...filters,ev_subtype:filters.ev_subtype===s.value?"":s.value})} className={`w-full p-3 rounded-xl text-sm font-medium text-left transition ${filters.ev_subtype===s.value?"bg-green-500 text-white":"bg-white text-gray-700 hover:bg-gray-100"}`}>{s.label}</button>))}</div></motion.div>)}

      {filters.vehicle_type&&vehicleSegments.length>0&&(<motion.div initial={{opacity:0,height:0}} animate={{opacity:1,height:"auto"}} className="mb-6"><h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Segment</h3><Select value={filters.vehicle_segment||"all"} onValueChange={(v)=>setFilters({...filters,vehicle_segment:v==="all"?"":v})}><SelectTrigger className="bg-white border-none rounded-xl h-12 shadow-sm"><SelectValue placeholder="All Segments"/></SelectTrigger><SelectContent><SelectItem value="all">All Segments</SelectItem>{vehicleSegments.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></motion.div>)}

      <div className="mb-6"><h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Brand</h3><SearchableDropdown options={brandOptions} value={filters.brand_id} onValueChange={(v)=>setFilters({...filters,brand_id:v,model_id:''})} placeholder="All Brands" searchPlaceholder="Search by brand"/></div>

      {filters.brand_id&&filteredModels.length>0&&(<motion.div initial={{opacity:0,height:0}} animate={{opacity:1,height:"auto"}} className="mb-6"><h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Model</h3><SearchableDropdown options={modelOptions} value={filters.model_id} onValueChange={(v)=>setFilters({...filters,model_id:v})} placeholder="All Models" searchPlaceholder="Search by model"/></motion.div>)}

      {filters.vehicle_type!=="ev"&&(<div className="mb-6"><h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Fuel Type</h3><div className="flex flex-wrap gap-2">{fuelTypes.map(f=>(<button key={f.value} onClick={()=>setFilters({...filters,fuel_type:filters.fuel_type===f.value?"":f.value})} className={`px-4 py-2 rounded-full text-sm font-medium transition ${filters.fuel_type===f.value?"bg-blue-600 text-white":"bg-white text-gray-700"}`}>{f.label}</button>))}</div></div>)}

      <div className="mb-6"><h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Transmission</h3><div className="flex flex-wrap gap-2">{transmissions.map(t=>(<button key={t.value} onClick={()=>setFilters({...filters,transmission:filters.transmission===t.value?"":t.value})} className={`px-4 py-2 rounded-full text-sm font-medium transition ${filters.transmission===t.value?"bg-blue-600 text-white":"bg-white text-gray-700"}`}>{t.label}</button>))}</div></div>

      <details className="mb-4">
        <summary className="text-xs font-semibold text-gray-500 uppercase cursor-pointer flex justify-between">More Filters <span>+</span></summary>
        <div className="space-y-4 mt-4">
          <div><h4 className="text-xs text-gray-500 mb-2">Color</h4><Select value={filters.color||"all"} onValueChange={(v)=>setFilters({...filters,color:v==="all"?"":v})}><SelectTrigger className="bg-white border-none rounded-xl h-12 shadow-sm"><SelectValue placeholder="All Colors"/></SelectTrigger><SelectContent><SelectItem value="all">All Colors</SelectItem>{colors.map(c=><SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent></Select></div>
          <div><h4 className="text-xs text-gray-500 mb-2">Ownership</h4><Select value={filters.ownership?.toString()||"all"} onValueChange={(v)=>setFilters({...filters,ownership:v==="all"?null:parseInt(v)})}><SelectTrigger className="bg-white border-none rounded-xl h-12 shadow-sm"><SelectValue placeholder="Any Owner"/></SelectTrigger><SelectContent><SelectItem value="all">Any Owner</SelectItem><SelectItem value="1">1st Owner</SelectItem><SelectItem value="2">2nd Owner</SelectItem><SelectItem value="3">3rd Owner</SelectItem><SelectItem value="4">4+ Owners</SelectItem></SelectContent></Select></div>
        </div>
      </details>
    </>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Header selectedCity={selectedCity} setSelectedCity={setSelectedCity} cities={cities} />
      <MobileFilterPanel open={mobileFilterOpen} onClose={() => setMobileFilterOpen(false)}>
        <FilterBody onClear={() => setMobileFilterOpen(false)} />
      </MobileFilterPanel>

      <div className="px-3 sm:px-6 md:px-8 lg:px-12 py-6 lg:py-8">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <h1 className="text-2xl md:text-4xl font-bold text-gray-800 mb-1">Find Your Perfect <span className="text-orange-500">Vehicle</span></h1>
          <p className="text-gray-500 text-sm md:text-base">Browse through our collection of quality vehicles</p>
        </motion.div>

        <div className="mb-4 bg-white rounded-2xl shadow-sm p-3 sm:p-4">
          <div className="flex items-center gap-2 border rounded-xl px-3 py-2.5 mb-3">
            <Search className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <input type="text" placeholder="Search brand, model, fuel..." defaultValue={searchQuery} onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/browse?q=${e.target.value}`); }} className="flex-1 outline-none text-sm min-w-0" />
            <Button size="sm" className="flex-shrink-0 text-xs px-3 h-8" onClick={(e) => { const input = e.currentTarget.parentElement.querySelector('input'); if (input.value.trim()) navigate(`/browse?q=${input.value}`); }}>Search</Button>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {vehicleTypes.map(type => (<button key={type.value} onClick={() => navigate(`/browse?type=${type.value}`)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition whitespace-nowrap ${filters.vehicle_type === type.value ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}>{type.label}</button>))}
            <button onClick={() => setFilters(prev => ({ ...prev, verified_only: !prev.verified_only }))} className={`px-3 py-1.5 rounded-full text-xs font-medium transition flex items-center gap-1 whitespace-nowrap ${filters.verified_only ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}><Shield className="h-3 w-3" /> Verified</button>
            <button onClick={() => navigate('/browse')} className="px-3 py-1.5 rounded-full text-xs font-medium bg-red-100 text-red-600 hover:bg-red-200 whitespace-nowrap">Clear</button>
          </div>
        </div>

        {(searchQuery || segmentSearch || registrationSearch) && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-4">
            <div className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-sm border">
              <Search className="h-4 w-4 text-gray-400" />
              <span className="text-gray-600 text-sm">Searching for:</span>
              <span className="font-medium text-orange-500 text-sm">"{registrationSearch || segmentSearch || searchQuery}"</span>
              <button onClick={() => navigate('/browse')} className="ml-2 p-1 rounded-full hover:bg-gray-100"><X className="h-4 w-4 text-gray-500" /></button>
            </div>
          </motion.div>
        )}

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          <motion.aside initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }} className="hidden lg:block lg:w-80">
            <div className="bg-[#eef1f6] rounded-2xl p-6 sticky top-4 shadow-sm"><FilterBody /></div>
          </motion.aside>

          <main className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-4 lg:mb-6 gap-3">
              <div className="min-w-0">
                <h1 className="text-xl lg:text-3xl font-bold text-secondary-800 mb-0.5">Browse Vehicles</h1>
                <p className="text-secondary-600 text-xs lg:text-sm truncate">
                  {isInitialLoading ? 'Loading...' : `${vehicles.length} vehicles in ${selectedCity}${hasMore ? ' — scroll for more' : ''}`}
                  {filters.verified_only && <span className="inline-flex items-center gap-1 ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold"><Shield className="h-3 w-3"/>Verified</span>}
                </p>
              </div>
              <button onClick={() => setMobileFilterOpen(true)} className="lg:hidden flex-shrink-0 flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 rounded-xl shadow-sm text-sm font-medium text-gray-700 hover:border-gray-300 transition relative">
                <SlidersHorizontal className="h-4 w-4 text-gray-500" />All Filters
                {activeFilterCount > 0 && (<span className="absolute -top-1.5 -right-1.5 bg-blue-600 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{activeFilterCount}</span>)}
              </button>
            </div>

            {isInitialLoading && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                {Array.from({length: PAGE_SIZE}).map((_,i) => <SkeletonCard key={i}/>)}
              </div>
            )}

            {!isInitialLoading && vehicles.length === 0 && (
              <Card><CardContent className="p-12 text-center">
                {filters.verified_only ? (
                  <><Shield className="h-12 w-12 text-blue-300 mx-auto mb-3"/><p className="text-secondary-600 text-lg mb-2">No verified vehicles found in {selectedCity}</p><Button onClick={()=>setFilters(prev=>({...prev,verified_only:false}))}>Show All Vehicles</Button></>
                ) : (
                  <><p className="text-secondary-600 text-lg">No vehicles found matching your criteria</p><Button onClick={clearFilters} className="mt-4 bg-primary-500 text-black hover:bg-primary-600">Clear Filters</Button></>
                )}
              </CardContent></Card>
            )}

            {!isInitialLoading && vehicles.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                {vehicles.map((vehicle, idx) => {
                  const isWishlisted = wishlistedIds.has(vehicle.id);
                  return (
                    <motion.div key={vehicle.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(idx * 0.04, 0.3) }}>
                      <Card onClick={() => navigate(`/vehicles/${vehicle.id}`)} className="cursor-pointer hover:shadow-lg transition-all duration-300 rounded-xl overflow-hidden border border-gray-200">
                        <div className="relative h-52 overflow-hidden bg-gray-100">
                          <img
                            src={vehicle.images?.[0] || "https://images.unsplash.com/photo-1676288176820-a5a954d81e6e?w=500"}
                            alt={`${vehicle.brand_name} ${vehicle.model_name}`}
                            className="w-full h-full object-contain transition-transform duration-300"
                            loading="lazy"
                          />
                          {vehicle.verified_by_x7autoz && <VerifiedBadge />}
                          <button onClick={(e)=>{e.stopPropagation();handleAddToWishlist(vehicle.id);}} className={`absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center shadow-md transition ${isWishlisted?"bg-red-500":"bg-white hover:bg-gray-100"}`}>
                            <Heart className={`h-4 w-4 ${isWishlisted?"text-white fill-white":"text-gray-600"}`}/>
                          </button>
                        </div>
                        <CardContent className="p-3">
                          <div className="flex justify-between items-start">
                            <h3 className="text-base font-semibold text-gray-800 leading-tight">{vehicle.brand_name} {vehicle.model_name}</h3>
                            <span className="text-amber-900 font-bold text-base">₹ {vehicle.price}</span>
                          </div>
                          <p className="text-xs text-gray-700 mt-1">{vehicle.variant_name}</p>
                          <div className="grid grid-cols-3 mt-3">
                            <div className="relative flex flex-col items-center px-3 pb-4 border-r border-gray-300"><Gauge className="h-4 w-4 text-black mb-1"/><span className="text-[10px] text-black">{vehicle.km_run_range||"0 KM"}</span><div className="absolute bottom-0 left-3 right-3 border-b border-gray-300"/></div>
                            <div className="relative flex flex-col items-center px-3 pb-4 border-r border-gray-300">{vehicle.fuel_type?.toLowerCase().includes("electric")?<Zap className={`h-4 w-4 mb-1 ${getFuelColor(vehicle.fuel_type)}`} fill="currentColor"/>:<Fuel className={`h-4 w-4 mb-1 ${getFuelColor(vehicle.fuel_type)}`}/>}<span className={`text-[10px] ${getFuelColor(vehicle.fuel_type)}`}>{vehicle.fuel_type}</span><div className="absolute bottom-0 left-3 right-3 border-b border-gray-300"/></div>
                            <div className="relative flex flex-col items-center px-3 pb-4"><Settings className="h-4 w-4 text-black mb-1"/><span className="text-[10px] text-black">{vehicle.transmission||"Manual"}</span><div className="absolute bottom-0 left-3 right-3 border-b border-gray-300"/></div>
                            <div className="flex flex-col items-center px-3 pt-3 border-r border-gray-300"><MapPin className="h-4 w-4 text-black mb-1"/><span className="text-[10px] text-black">{vehicle.city||"City"}</span></div>
                            <div className="flex flex-col items-center px-3 pt-3 border-r border-gray-300"><Calendar className="h-4 w-4 text-black mb-1"/><span className="text-[10px] text-black">{vehicle.manufacturing_year||"2022"}</span></div>
                            <div className="flex flex-col items-center px-3 pt-3"><Hash className="h-4 w-4 text-black mb-1"/><span className="text-[10px] text-black">{vehicle.registration_no||"KA-XX"}</span></div>
                          </div>
                          <div className="flex gap-2 mt-4 pt-3">
                            <Button className="flex-1 bg-white hover:bg-gray-100 border border-gray-300 text-black text-xs h-9" onClick={(e)=>{e.stopPropagation();if(!user)return navigate("/login");window.open(`tel:${vehicle.contact_phone}`);}}><Phone className="h-3.5 w-3.5 mr-1"/>Call</Button>
                            <Button className="flex-1 bg-green-500 hover:bg-green-600 text-white text-xs h-9" onClick={(e)=>{e.stopPropagation();if(!user)return navigate("/login");const msg=encodeURIComponent(`Hi, I'm interested in your ${vehicle.brand_name} ${vehicle.model_name}`);window.open(`https://wa.me/${vehicle.contact_phone}?text=${msg}`,"_blank");}}><FaWhatsapp className="h-3.5 w-3.5 mr-1"/>WhatsApp</Button>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            )}

            <div ref={sentinelRef} className="mt-8 flex flex-col items-center gap-3">
              {isFetchingMore && (<div className="flex items-center gap-3 py-6">{[0,1,2].map(i=>(<span key={i} className="w-2.5 h-2.5 rounded-full bg-blue-500 inline-block" style={{animation:`bounce 0.8s ease-in-out ${i*0.15}s infinite`}}/>))}<span className="text-sm text-gray-500 ml-1">Loading more vehicles...</span></div>)}
              {!hasMore && !isInitialLoading && vehicles.length > 0 && (<p className="text-sm text-gray-400 py-6">You've seen all {vehicles.length} vehicles</p>)}
            </div>
            <style>{`@keyframes bounce{0%,100%{transform:translateY(0);opacity:0.6;}50%{transform:translateY(-8px);opacity:1;}}`}</style>
          </main>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Browse;